import { useEffect } from "react";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { SectionLayout } from "@/components/SectionLayout";
import { Heading, Text } from "@/components/Text";
import { Button } from "@/components/Button";
import { getProgram } from "../../sanity/lib/getCmsData";
import { urlForImage } from "../../sanity/lib/image";

export async function getStaticProps() {
  const data = await getProgram();
  return {
    props: {
      data,
    },
    revalidate: 10,
  };
}

export default function TheProgram({ data }) {
  const industriesBlockData = data.industriesBlock;
  const phasesBlockData = data.phasesBlock;
  const testimonialsBlockData = data.testimonialsBlock;

  useEffect(() => {
    import("aos").then((AOS) => {
      import("aos/dist/aos.css");
      AOS.default.init({ duration: 500, easing: "ease-in", once: true, delay: 25 });
    });
  }, []);
  return (
    <>
      <Head>
        <title>The Program | Red Ball Drills</title>
        <meta
          name="description"
          content="Red Ball Drills help industries from law enforcement to Fortune 500 companies."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <Industries data={industriesBlockData} />
      <Phases data={phasesBlockData} />
      <Testimonials data={testimonialsBlockData} />
    </>
  );
}

function Industries({ data }) {
  const { title, industries } = data;
  return (
    <SectionLayout>
      <Heading data-aos="fade-in" size="page" className="mb-6">
        {title}
      </Heading>
      <section className="mb-9 md:mb-10 flex flex-wrap justify-center gap-5 md:gap-7">
        {!!industries?.length &&
          industries.map((industry) => {
            return (
              <IndustryCard
                key={industry._key}
                caseStudyLink={industry?.tag?.title}
              >
                {industry.title}
              </IndustryCard>
            );
          })}
      </section>
      <div className="flex justify-center">
        <Button role="contact" />
      </div>
    </SectionLayout>
  );
}

function IndustryCard({ children, caseStudyLink = "" }) {
  return (
    <Link href={`/case-studies#${caseStudyLink}`}>
      <article
        // data-aos="fade-in"
        // data-aos-duration="800"
        className="w-[16.875rem] h-[11.625rem] px-8 flex justify-center items-center text-center rounded-xl border-[1px] shadow-surround bg-rbd-base hover:scale-105 duration-200 ease-in"
      >
        <Text size="industry-card">{children}</Text>
      </article>
    </Link>
  );
}

function Phases({ data }) {
  const { title, phases } = data;

  return (
    <section className="bg-rbd-secondary-base overflow-hidden">
      <div className="w-full my-20 md:my-[6.25rem] xl:my-[7.5rem]">
        <div className="px-5 md:px-8 ">
          <div className="mx-auto max-w-[1170px] ">
            <Heading data-aos="fade-in" className="mb-6">
              {title}
            </Heading>
          </div>
        </div>
        {!!phases?.length &&
          phases.map((phase, index) => {
            const isEvenIndex = index % 2 === 0;
            return (
              <section key={phase._key} className="mb-8 md:mb-9">
                <div
                  data-aos={isEvenIndex ? "fade-right" : "fade-left"}
                  data-aos-duration="800"
                  className={
                    isEvenIndex
                      ? "flex w-full pr-5 md:pr-8"
                      : "flex flex-row-reverse w-full pl-5 md:pl-8"
                  }
                >
                  <article
                    className={
                      isEvenIndex
                        ? "w-full max-w-[798px] lg:max-w-full lg:w-[calc(((100%-1170px)/2)+887px)] py-10 pl-5 pr-10 flex justify-end bg-rbd-base rounded-r-xl shadow-surround"
                        : "w-full max-w-[798px] lg:max-w-full lg:w-[calc(((100%-1170px)/2)+887px)] py-10 pr-5 pl-10 bg-rbd-base rounded-l-xl shadow-surround"
                    }
                  >
                    <div className="lg:max-w-[845px] flex flex-col items-center md:flex-row gap-6 md:gap-4">
                      <Image
                        src={urlForImage(phase.mainImage).url()}
                        alt={phase.mainImage.caption}
                        width={134}
                        height={134}
                      />
                      <div>
                        <Heading as="h3" size="phase-card" className="mb-3">
                          {phase.title}
                        </Heading>
                        <Text>{phase.description}</Text>
                      </div>
                    </div>
                  </article>
                </div>
              </section>
            );
          })}
        <div className="flex justify-center">
          <Button role="contact" />
        </div>
      </div>
    </section>
  );
}

function Testimonials({ data }) {
  const { title, testimonials } = data;
  return (
    <SectionLayout>
      <Heading data-aos="fade-in" className="mb-6">
        {title}
      </Heading>
      <section className="mb-10 md:mb-6 flex flex-col flex-wrap items-center xl:flex-row xl:justify-center xl:items-stretch gap-8 xl:gap-7">
        {!!testimonials?.length &&
          testimonials.map((testimonial, i) => (
            <figure
              key={i}
              className="px-5 py-9 md:px-9 max-w-[570px] md:min-h-[406px] shadow-surround rounded-xl flex flex-col bg-rbd-base"
            >
              <figcaption className="min-h-[4.5rem] mb-2">
                {testimonial.details.map((detail, j) => (
                  <Heading
                    key={j}
                    as="p"
                    size="title"
                    className="text-rbd-tertiary"
                  >
                    {detail}
                  </Heading>
                ))}
              </figcaption>
              <blockquote>
                <Text>{testimonial.review}</Text>
              </blockquote>
            </figure>
          ))}
      </section>
    </SectionLayout>
  );
}
